/**
 * Horse / donkey model (vanilla AbstractEquineModel proportions, 64x64 sheet)
 * over the real vanilla sheets (entity/horse/*.png, markings overlay, donkey,
 * equipment/horse_saddle, equipment/horse_body). Saddle and armour re-render the
 * whole model, inflated, with the equipment sheet; they follow `view.saddled`
 * and the armour tier packed into `view.coat` (bits 8-10; bits 0-2 colour,
 * bits 3-5 markings, value 8 in bits 0-5 = donkey).
 */

import * as THREE from 'three';
import type { EnvUniforms } from '../env';
import type { MobView } from '../../game/types';
import { buildPart, entityMaterial, sheetTexture, type CubeDef, type EntityMat, type PartDef } from './core';
import type { QuadPose } from './mobs';

const PI = Math.PI;
const cube = (tex: [number, number], from: [number, number, number], size: [number, number, number], inflate = 0, mirror = false): CubeDef =>
  ({ tex, from, size, inflate, mirror });

export interface HorseAnim {
  parts: THREE.Object3D;
  mats: EntityMat[];
  head?: THREE.Object3D;
  animate(v: MobView, q: QuadPose, headY: number, headX: number, age: number): void;
  dispose(): void;
}

const COATS = ['white', 'creamy', 'chestnut', 'brown', 'black', 'gray', 'darkbrown'];
const MARKINGS = ['', 'white', 'whitefield', 'whitedots', 'blackdots'];
const ARMOR_SHEET = ['', 'leather', 'iron', 'gold', 'diamond'];
const LEATHER_DYE = 0xa06540;

/** One full AbstractEquineModel pass (vanilla 1.21 geometry) over `mat`; `g` inflates every box. */
function buildHorse(mat: EntityMat, g: number, donkey: boolean) {
  const root = new THREE.Group();
  const P = (d: PartDef, parent: THREE.Object3D = root): THREE.Group => { const n = buildPart(d, 64, 64, mat); parent.add(n); return n; };
  P({ cubes: [cube([0, 32], [-5, -8, -17], [10, 10, 22], 0.05 + g)], pos: [0, 11, 5] });
  const tail = P({ cubes: [cube([42, 36], [-1.5, 0, 0], [3, 14, 4], g)], pos: [0, 6, 7], rot: [PI / 6, 0, 0] });
  const headParts = P({ cubes: [cube([0, 35], [-2.05, -6, -2], [4, 12, 7], g)], pos: [0, 4, -12], rot: [PI / 6, 0, 0] });
  P({ cubes: [cube([0, 13], [-3, -11, -2], [6, 5, 7], g)] }, headParts);
  P({ cubes: [cube([56, 36], [-1, -11, 5.01], [2, 16, 2], g)] }, headParts);
  P({ cubes: [cube([0, 25], [-2, -11, -7], [4, 5, 5], g)] }, headParts);
  if (donkey) {
    // DonkeyModel long ears (2x7x1), splayed outward
    P({ cubes: [cube([0, 12], [-1, -7, 0], [2, 7, 1], g)], pos: [1.25, -10, 4], rot: [0.2617994, 0, 0.2617994] }, headParts);
    P({ cubes: [cube([0, 12], [-1, -7, 0], [2, 7, 1], g)], pos: [-1.25, -10, 4], rot: [0.2617994, 0, -0.2617994] }, headParts);
  } else {
    P({ cubes: [cube([19, 16], [0.55, -13, 4], [2, 3, 1], g - 0.001)] }, headParts);
    P({ cubes: [cube([19, 16], [-2.55, -13, 4], [2, 3, 1], g - 0.001)] }, headParts);
  }
  const leg = (x: number, z: number, front: boolean, mirror: boolean): THREE.Group =>
    P({ cubes: [cube([48, 21], [mirror ? -3 : -1, -1, front ? -1.9 : -1], [4, 11, 4], g, mirror)], pos: [x, 14, z] });
  const legs = [leg(4, 7, false, true), leg(-4, 7, false, false), leg(4, -12, true, true), leg(-4, -12, true, false)];
  return { root, tail, headParts, legs };
}

/**
 * Vanilla saddle layer (AbstractEquineModel saddle parts): the equipment sheet is NOT the
 * body UV layout, it has its own seat / head-gear boxes at the top of the sheet.
 */
function buildSaddle(mat: EntityMat): ReturnType<typeof buildHorse> {
  const root = new THREE.Group();
  const P = (d: PartDef, parent: THREE.Object3D = root): THREE.Group => { const n = buildPart(d, 64, 64, mat); parent.add(n); return n; };
  P({ cubes: [cube([26, 0], [-5, -8, -9], [10, 9, 9], 0.5)], pos: [0, 11, 5] });
  const headParts = P({ cubes: [], pos: [0, 4, -12], rot: [PI / 6, 0, 0] });
  P({ cubes: [cube([29, 5], [2, -9, -6], [1, 2, 2], 0)] }, headParts);
  P({ cubes: [cube([29, 5], [-3, -9, -6], [1, 2, 2], 0)] }, headParts);
  P({ cubes: [cube([32, 2], [3.1, -6, -8], [0, 3, 16], 0)], rot: [-PI / 6, 0, 0] }, headParts);
  P({ cubes: [cube([32, 2], [-3.1, -6, -8], [0, 3, 16], 0)], rot: [-PI / 6, 0, 0] }, headParts);
  P({ cubes: [cube([1, 1], [-3, -11, -1.9], [6, 5, 6], 0.22)] }, headParts);
  P({ cubes: [cube([19, 0], [-2, -11, -4], [4, 5, 2], 0.2)] }, headParts);
  const dummy = (): THREE.Group => new THREE.Group();
  return { root, tail: dummy(), headParts, legs: [dummy(), dummy(), dummy(), dummy()] };
}

export function horseAnim(env: EnvUniforms): HorseAnim {
  const parts = new THREE.Group();
  const mat = entityMaterial(env, sheetTexture('entity/horse/horse_brown'));
  const mark = entityMaterial(env, sheetTexture('entity/horse/horse_markings_white'));
  mark.transparent = true; mark.depthWrite = false;
  const sat = entityMaterial(env, sheetTexture('entity/equipment/horse_saddle/saddle'));
  const arm = entityMaterial(env, sheetTexture('entity/equipment/horse_body/iron'));
  const mats = [mat, mark, sat, arm];
  // saddle / armour are separate inflated passes, built once per body type
  let donkeyBuilt: boolean | null = null;
  let layers: ReturnType<typeof buildHorse>[] = [];
  const build = (donkey: boolean): void => {
    for (const l of layers) parts.remove(l.root);
    layers = [buildHorse(mat, 0, donkey), buildHorse(mark, 0.03, donkey), buildSaddle(sat), buildHorse(arm, 0.12, donkey)];
    for (const l of layers) parts.add(l.root);
    donkeyBuilt = donkey;
  };
  let lastKey = -1, lastArmor = -1;
  return {
    parts, mats,
    animate(v, q, headY, headX, age) {
      const coat = v.coat ?? 0;
      const key = coat & 63;
      const donkey = key === 8;
      if (donkey !== donkeyBuilt) { build(donkey); lastKey = -1; }
      if (key !== lastKey) {
        lastKey = key;
        const marking = key >> 3;
        mat.uniforms.uMap.value = sheetTexture(donkey ? 'entity/horse/donkey' : `entity/horse/horse_${COATS[(key & 7) % 7]}`);
        mark.uniforms.uMap.value = sheetTexture(`entity/horse/horse_markings_${MARKINGS[Math.min(marking, 4)] || 'white'}`);
        layers[1].root.visible = !donkey && marking > 0;
        sat.uniforms.uMap.value = sheetTexture(donkey ? 'entity/equipment/donkey_saddle/saddle' : 'entity/equipment/horse_saddle/saddle');
      }
      const tier = (coat >> 8) & 7;
      if (tier !== lastArmor) {
        lastArmor = tier;
        arm.uniforms.uMap.value = sheetTexture(`entity/equipment/horse_body/${ARMOR_SHEET[tier] || 'iron'}`);
        arm.uniforms.uTint.value.setHex(tier === 1 ? LEATHER_DYE : 0xffffff);
      }
      layers[2].root.visible = !!v.saddled;
      layers[3].root.visible = tier > 0 && !donkey;
      const s = q.limbSwing * 0.6662, a = Math.min(q.limbAmt, 1) * 1.2;
      const hx = PI / 6 + headX * 0.6, hy = headY * 0.8;
      for (const l of layers) {
        l.legs[0].rotation.x = Math.cos(s) * a; l.legs[1].rotation.x = Math.cos(s + PI) * a;
        l.legs[2].rotation.x = Math.cos(s + PI) * a; l.legs[3].rotation.x = Math.cos(s) * a;
        l.headParts.rotation.x = hx; l.headParts.rotation.y = hy;
        l.tail.rotation.x = PI / 6 + Math.sin(age * 0.1) * 0.05 + a * 0.2;
      }
      // HorseRenderer scale: horse 1.1, donkey 0.87
      const sc = donkey ? 0.87 : 1.1;
      parts.scale.set(sc, sc, sc);
      parts.position.y = 24 * (1 - sc);
    },
    dispose() { for (const m of mats) m.dispose(); },
  };
}
